/**
 * Construye el detalle que acompana al aviso cuando una asociacion o
 * desasociacion de empleados falla (CEC-668).
 *
 * El backend explica POR QUE falla, pero lo dice en ingles y con el Id interno
 * ("The Partner with Id = 9624 does not exist in the system."), asi que no se
 * puede enseniar tal cual a un comercial. Aqui se traduce lo conocido y se
 * corta lo desconocido, que en el camino del PATCH es el `ex.Message` de una
 * excepcion .NET — lo ultimo que debe acabar en pantalla.
 */

export interface SetParentFailure {
  partnerId: number;
  reason: string;
}

/** Cuantos fallos se enumeran antes de resumir el resto como «y N mas». */
export const MAX_LISTED_FAILURES = 3;

const UNKNOWN_REASON = 'motivo no especificado';

/** Nombre de cortesia cuando el contacto ya no esta en las listas cargadas. */
const UNKNOWN_EMPLOYEE = 'Un contacto';

/**
 * Motivos conocidos del backend, en el orden en que se comprueban.
 *
 * Hoy solo hay uno porque `PATCH /api/partner` unicamente valida que el
 * contacto exista. Las reglas de negocio —no es empleado, ya pertenece a otra
 * empresa, duplicado— viven en el endpoint en lote (CEC-598), que todavia no
 * existe: cuando se construya, sus mensajes se anaden a esta tabla.
 */
const KNOWN_REASONS: Array<{ match: RegExp; text: string }> = [
  { match: /does not exist in the system/i, text: 'el contacto ya no existe' },
];

/**
 * Mensajes que el interceptor de errores ya devuelve en castellano
 * (standarize-errors.interceptor, getUserMessage). Son utiles y seguros de
 * mostrar, asi que pasan tal cual.
 *
 * Si alli cambian, esto degrada a «motivo no especificado»: se pierde detalle,
 * no se ensenia nada que no toque.
 */
const SAFE_GENERIC_REASONS = new Set([
  'No se pudo conectar al servidor',
  'Solicitud inválida',
  'No autorizado',
  'Acceso prohibido',
  'Recurso no encontrado',
  'Error interno del servidor',
]);

/** Traduce el motivo crudo del backend a algo que el comercial entienda. */
export function humanizeSetParentReason(reason: string | null | undefined): string {
  const raw = (reason || '').trim();
  if (!raw) return UNKNOWN_REASON;

  const known = KNOWN_REASONS.find((candidate) => candidate.match.test(raw));
  if (known) return known.text;

  if (SAFE_GENERIC_REASONS.has(raw)) return raw;

  // Mensaje no previsto: puede ser el texto de una excepcion del servidor.
  return UNKNOWN_REASON;
}

/**
 * Detalle del aviso: «Nombre: motivo», separados por · y con tope.
 *
 * El tope existe porque el aviso es un toast, no un informe: con diez fallos
 * la lista completa lo convierte en una pared de texto que nadie lee.
 */
export function buildSetParentFailureDetail(
  failed: SetParentFailure[],
  nameOf: (partnerId: number) => string,
  maxListed: number = MAX_LISTED_FAILURES
): string {
  if (!failed?.length) return '';

  const listed = failed
    .slice(0, maxListed)
    .map((failure) => {
      const name = (nameOf(failure.partnerId) || '').trim() || UNKNOWN_EMPLOYEE;
      return `${name}: ${humanizeSetParentReason(failure.reason)}`;
    });

  const remaining = failed.length - listed.length;

  return remaining > 0
    ? `${listed.join(' · ')} · y ${remaining} más`
    : listed.join(' · ');
}

/** «1 no se pudo asociar» / «2 no se pudieron asociar»: la frase concuerda. */
export function buildPartialFailureMessage(ok: number, total: number, failed: number): string {
  const failedPhrase =
    failed === 1 ? '1 no se pudo asociar' : `${failed} no se pudieron asociar`;

  return `Se asociaron ${ok} de ${total} empleados. ${failedPhrase}.`;
}
